import { css } from '@emotion/react';

export const HANDLE = {
  ALL: ['n', 'nw', 'ne', 's', 'se', 'sw', 'e', 'w'],
  LEFT_RIGHT: ['e', 'w'],
  TOP_BOTTOM: ['n', 's'],
  TOP_BOTTOM_LEFT_RIGHT: ['n', 's', 'e', 'w'],
  CORNERS: ['nw', 'ne', 'se', 'sw'],
  SIDE_CORNERS: ['e', 'w', 'nw', 'ne', 'se', 'sw'],
  TOP_LEFT_RIGHT: ['nw', 'e'],
  TOP_BOTTOM_CORNERS: ['n', 's', 'nw', 'ne', 'se', 'sw'],
  NONE: [],
};

export enum Direction {
  NORTH = 'NORTH',
  NORTH_EAST = 'NORTH_EAST',
  NORTH_WEST = 'NORTH_WEST',
  EAST = 'EAST',
  WEST = 'WEST',
  SOUTH = 'SOUTH',
  SOUTH_WEST = 'SOUTH_WEST',
  SOUTH_EAST = 'SOUTH_EAST',
}

export const DIRECTION_HANDLE_MAP = {
  [Direction.NORTH]: 'n',
  [Direction.NORTH_EAST]: 'ne',
  [Direction.NORTH_WEST]: 'nw',
  [Direction.EAST]: 'e',
  [Direction.WEST]: 'w',
  [Direction.SOUTH]: 's',
  [Direction.SOUTH_WEST]: 'sw',
  [Direction.SOUTH_EAST]: 'se',
};

export const DIRECTION_MAP = {
  [Direction.NORTH]: [0, -1],
  [Direction.NORTH_EAST]: [1, -1],
  [Direction.NORTH_WEST]: [-1, -1],
  [Direction.EAST]: [1, 0],
  [Direction.WEST]: [-1, 0],
  [Direction.SOUTH]: [0, 1],
  [Direction.SOUTH_WEST]: [-1, 1],
  [Direction.SOUTH_EAST]: [1, 1],
};

export const LOCK_COLOR = 'var(--vg-colors-red-600)';
export const WIDGETBASE_CLASS = 'widget-container';
export const EDITABLE_WIDGET_CLASS = 'widget-editable';
export const WIDGET_LOCK_CLASS = 'widget-locked';
export const MOVEABLE_LOCK_CLASS = 'moveable-locked';
export const BOUNDING_BOX_CLASS = 'moveable-control-box';
export const SELECTO_TARGET_CLASS = 'widget-selecto-target';
export const PORT_CONTAINER_CLASS = 'port-container';
export const PORT_SELECTOR_CLASS = 'port-selector';
export const DRAG_HANDLE_CLASS = 'custom-drag-handle-control';
export const DRAG_HANDLE_ID_PREFIX = 'custom-drag-handle-';
export const IMPROVE_BUTTON_ID_PREFIX = 'custom-improve-button-handle-';
export const IGNORE_TARGET_CLASS = 'ignore-selecto-target';
export const IGNORE_SMART_GUIDE_CLASS = 'ignore-smart-guide-target';
export const PORT_CLASS = 'port';

// Class name for react-moveable bounding box area when multiple
// widgets are selected
export const MOVEABLE_MULTISELECT_CLASS = 'moveable-area';

// Bounding box handle sizes
export const HORIZONTAL_RESIZE_HANDLE_WIDTH_PX = 8;
export const HORIZONTAL_RESIZE_HANDLE_HEIGHT_PX = 24;
export const HORIZONTAL_RESIZE_MARGIN_TOP_PX = HORIZONTAL_RESIZE_HANDLE_HEIGHT_PX * -0.5;
export const HORIZONTAL_RESIZE_MARGIN_LEFT_PX = HORIZONTAL_RESIZE_HANDLE_WIDTH_PX * -0.5;

export const VERTICAL_RESIZE_HANDLE_WIDTH_PX = 24;
export const VERTICAL_RESIZE_HANDLE_HEIGHT_PX = 8;
export const VERTICAL_RESIZE_MARGIN_TOP_PX = VERTICAL_RESIZE_HANDLE_HEIGHT_PX * -0.5;
export const VERTICAL_RESIZE_MARGIN_LEFT_PX = VERTICAL_RESIZE_HANDLE_WIDTH_PX * -0.5;

// Small handle sizes - for smaller bounding boxes
const SMALL_SIDE_HANDLE = 6;
const SMALL_RESIZE_HANDLE_HEIGHT = 16;
const SMALL_RESIZE_HANDLE_MARGIN_TOP = SMALL_RESIZE_HANDLE_HEIGHT * -0.5;
const SMALL_RESIZE_HANDLE_MARGIN_LEFT_PX = SMALL_SIDE_HANDLE * -0.5;

// Used when need to move cursor further down to account for flow mode ports
export const ROTATION_CURSOR_EXTRA_MARGIN_TOP_PX = -14;
export const ROTATION_CURSOR_MARGIN_TOP_PX = 4;

export const BOUNDING_BOX_COLORS_PRIMARY = 'var(--vg-colors-upgrade-500)';
export const BOUNDING_BOX_LINE_COLOR = 'var(--vg-colors-upgrade-500)';

// Bounding box base styles
export const baseBoundingBoxCssStyle = css`
  .moveable-control.moveable-direction {
    border: 1.5px solid ${BOUNDING_BOX_COLORS_PRIMARY};
    background: var(--vg-colors-white);
    transition: background 0.333s;

    &:hover {
      background: ${BOUNDING_BOX_COLORS_PRIMARY};
    }
  }
  .moveable-control.moveable-resizable.moveable-e,
  .moveable-control.moveable-resizable.moveable-w,
  .moveable-control.moveable-resizable.moveable-n,
  .moveable-control.moveable-resizable.moveable-s {
    border-radius: 4px;
  }

  .moveable-control.moveable-resizable.moveable-e,
  .moveable-control.moveable-resizable.moveable-w {
    width: ${HORIZONTAL_RESIZE_HANDLE_WIDTH_PX}px;
    height: ${HORIZONTAL_RESIZE_HANDLE_HEIGHT_PX}px;
    margin-top: ${HORIZONTAL_RESIZE_MARGIN_TOP_PX}px;
    margin-left: ${HORIZONTAL_RESIZE_MARGIN_LEFT_PX}px;
  }
  .moveable-control.moveable-resizable.moveable-n,
  .moveable-control.moveable-resizable.moveable-s {
    width: ${VERTICAL_RESIZE_HANDLE_WIDTH_PX}px;
    height: ${VERTICAL_RESIZE_HANDLE_HEIGHT_PX}px;
    margin-top: ${VERTICAL_RESIZE_MARGIN_TOP_PX}px;
    margin-left: ${VERTICAL_RESIZE_MARGIN_LEFT_PX}px;
  }
  .moveable-edgeDraggable.moveable-line.moveable-direction {
    height: 1px;
  }
  // For widget bounding box inside a multi-selection bounding box
  .moveable-line.moveable-direction {
    height: 1px;
    background: ${BOUNDING_BOX_LINE_COLOR};
  }

  .moveable-line.moveable-direction:hover {
    cursor: move;
  }
`;

export const hiddenHorizontalHandleCss = css`
  .moveable-control.moveable-resizable.moveable-e,
  .moveable-control.moveable-resizable.moveable-w {
    opacity: 0;
    width: ${SMALL_SIDE_HANDLE}px !important;
    height: ${SMALL_RESIZE_HANDLE_HEIGHT}px !important;
    margin-top: ${SMALL_RESIZE_HANDLE_MARGIN_TOP}px !important;
    margin-left: ${SMALL_RESIZE_HANDLE_MARGIN_LEFT_PX}px !important;
  }
`;

export const hiddenVerticalHandleCss = css`
  .moveable-control.moveable-resizable.moveable-n,
  .moveable-control.moveable-resizable.moveable-s {
    opacity: 0;
    width: ${SMALL_RESIZE_HANDLE_HEIGHT}px !important;
    height: ${SMALL_SIDE_HANDLE}px !important;
    margin-left: ${HORIZONTAL_RESIZE_MARGIN_TOP_PX}px !important;
    margin-top: ${SMALL_RESIZE_HANDLE_MARGIN_LEFT_PX}px !important;
  }
`;

export const hiddenVerticalHorizontalHandleCss = css`
  ${hiddenVerticalHandleCss}${hiddenHorizontalHandleCss}
`;
